import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";

const REQUEST_TYPES = [
  { id: "access", label: "Πρόσβαση στα δεδομένα μου" },
  { id: "rectification", label: "Διόρθωση των δεδομένων μου" },
  { id: "erasure", label: "Διαγραφή των δεδομένων μου" },
  { id: "portability", label: "Φορητότητα (αντίγραφο) των δεδομένων μου" },
  { id: "objection", label: "Εναντίωση στην επεξεργασία" },
  { id: "withdraw", label: "Ανάκληση της συναίνεσής μου" },
] as const;

function DataRequestPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState<string>("access");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error("Συμπλήρωσε ονοματεπώνυμο και email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Συμπλήρωσε ένα έγκυρο email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/data-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, email, requestType, details }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        toast.error((b as { error?: string }).error || "Κάτι πήγε στραβά.");
        return;
      }
      setSent(true);
      toast.success("Λάβαμε το αίτημά σου! 🐼");
    } catch {
      toast.error("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-10">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">🐼</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <ShieldCheck className="h-4 w-4" /> Τα δικαιώματά σου, στα χέρια σου
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight">Αίτημα για τα δεδομένα μου</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Συμπλήρωσε τη φόρμα για να ασκήσεις τα δικαιώματά σου βάσει GDPR. Θα απαντήσουμε το αργότερο εντός ενός μηνός.
            Δες και την{" "}
            <Link to="/privacy" className="text-primary hover:underline">Πολιτική Απορρήτου</Link>.
          </p>
        </div>

        {sent ? (
          <div className="mt-8 animate-pop-in rounded-3xl border border-success/30 bg-success/10 p-8 text-center">
            <p className="text-4xl">🎉</p>
            <h2 className="mt-2 text-xl font-semibold">Το αίτημά σου καταχωρήθηκε!</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Σου στείλαμε επιβεβαίωση στο email σου. Θα επικοινωνήσουμε σύντομα μαζί σου.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-border bg-card p-6 shadow-sm">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Ονοματεπώνυμο *</span>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-background px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Email *</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" className="h-10 w-full rounded-xl border border-border bg-background px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Τύπος αιτήματος *</span>
              <select value={requestType} onChange={(e) => setRequestType(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-background px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
                {REQUEST_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Λεπτομέρειες (προαιρετικά)</span>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={5000} className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" placeholder="Πες μας περισσότερα για το αίτημά σου..." />
            </label>
            <button type="submit" disabled={submitting} className="mp-bouncy w-full rounded-full bg-gradient-to-r from-primary to-amber-500 py-3 font-bold text-primary-foreground shadow-md shadow-primary/20 hover:opacity-95 disabled:opacity-50">
              {submitting ? "Αποστολή..." : "Αποστολή αιτήματος 🚀"}
            </button>
          </form>
        )}
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/data-request")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { property: "og:url", content: "https://mrpanda.gr/data-request" },
      { title: "Αίτημα για τα δεδομένα μου — MrPanda.gr" },
      { name: "description", content: "Άσκησε τα δικαιώματά σου βάσει GDPR: πρόσβαση, διόρθωση, διαγραφή ή φορητότητα των προσωπικών σου δεδομένων." },
      { property: "og:title", content: "Αίτημα για τα δεδομένα μου — MrPanda.gr" },
      { property: "og:description", content: "Άσκησε τα δικαιώματά σου βάσει GDPR: πρόσβαση, διόρθωση, διαγραφή ή φορητότητα των προσωπικών σου δεδομένων." },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/data-request" }],
  }),
  component: DataRequestPage,
});
